import { useEffect, useState } from "react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { EditableContent } from "../components/EditableContent";
import { NumberChallenge } from "../components/NumberChallenge";
import { api, formatApiError } from "../lib/api";
import { Mail, CheckCircle2 } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [human, setHuman] = useState(false);

  useEffect(() => { document.title = "Contact Us | Tenverly"; }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!human) { setError("Please enter the confirmation number to prove you're human."); return; }
    setLoading(true);
    try { await api.post("/contact", form); setDone(true); }
    catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="App bg-[#F4F7FA] min-h-screen">
      <Navbar />
      <div className="max-w-lg mx-auto px-5 py-16">
        <div className="flex items-center gap-2 mb-2"><Mail className="w-6 h-6 text-mint-dark" /><h1 className="font-heading font-extrabold text-3xl text-navy">Contact us</h1></div>
        <EditableContent file="contact.html" className="text-slate-600"
          fallback={`<p>Questions, corrections or a dispute about a review? Send us a message and our team will get back to you.</p>`} />

        {done ? (
          <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-8 text-center shadow-sm" data-testid="contact-success">
            <CheckCircle2 className="w-12 h-12 text-mint mx-auto" />
            <h2 className="mt-3 font-heading font-bold text-xl text-navy">Message sent</h2>
            <p className="mt-2 text-slate-600">Thanks {form.name.split(" ")[0] || ""} — we've received your message and will reply to {form.email}.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 shadow-sm flex flex-col gap-4" data-testid="contact-form">
            <div>
              <label className="text-sm font-medium text-slate-700">Your name</label>
              <input data-testid="contact-name" required value={form.name} onChange={set("name")}
                className="mt-1 w-full border border-slate-300 rounded-xl px-3 py-2.5 outline-none focus:ring-2 focus:ring-mint" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Email</label>
              <input data-testid="contact-email" type="email" required value={form.email} onChange={set("email")}
                className="mt-1 w-full border border-slate-300 rounded-xl px-3 py-2.5 outline-none focus:ring-2 focus:ring-mint" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Subject</label>
              <input data-testid="contact-subject" value={form.subject} onChange={set("subject")}
                className="mt-1 w-full border border-slate-300 rounded-xl px-3 py-2.5 outline-none focus:ring-2 focus:ring-mint" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Message</label>
              <textarea data-testid="contact-message" required rows={5} value={form.message} onChange={set("message")}
                className="mt-1 w-full border border-slate-300 rounded-xl px-3 py-2.5 outline-none focus:ring-2 focus:ring-mint" />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="contact-error">{error}</p>}
            <NumberChallenge onValidChange={setHuman} />
            <button data-testid="contact-submit" disabled={loading}
              className="bg-mint text-white font-semibold py-3 rounded-xl hover:bg-mint-dark transition-colors disabled:opacity-60">
              {loading ? "Sending…" : "Send message"}
            </button>
          </form>
        )}
      </div>
      <Footer />
    </div>
  );
}
